import type { EstimatedDeliveryProps } from '@athena-lib/EstimatedDelivery';
import type { OrderTotalProps } from '@athena-lib/OrderTotal/OrderTotal.svelte';
import type {
  CartShipping,
  CheckoutPageShipping,
  CheckoutStateData,
  createCheckoutInstance,
} from '@stickerapp-org/checkout-sdk';

type checkoutInstance = ReturnType<typeof createCheckoutInstance>;
export type CheckoutSdk = {
  state: CheckoutStateData;
} & Omit<checkoutInstance, 'state'>;
export type CheckoutState = CheckoutSdk['state'];
export type CheckoutActions = CheckoutSdk['actions'];

export const createCurrencyFormatter = ({
  language,
  currency,
}: {
  language: string;
  currency: string;
}) => {
  const { format } = new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  });

  return (value: number) => format(value / 100);
};

export const createDateFormatter = ({ language }: { language: string }) =>
  new Intl.DateTimeFormat(language, {
    weekday: 'short',
    day: 'numeric',
  }).format;

export const formatEstimatedDeliveryDates = (
  formatter: ReturnType<typeof createDateFormatter>,
  shippingMethod: CheckoutPageShipping['selectedShippingMethod'],
) => {
  if (!shippingMethod) {
    return undefined;
  }

  const { minDate, maxDate } = shippingMethod.deliveryEstimate;
  const formattedMin = formatter(minDate);
  const formattedMax = formatter(maxDate);

  if (formattedMin === formattedMax) {
    return { formattedDeliveryDate: formattedMin, dateInterval: false };
  }

  return { formattedDeliveryDate: `${formattedMin} - ${formattedMax}`, dateInterval: true };
};

export const isCheckoutPageShipping = (shipping?: CartShipping): shipping is CheckoutPageShipping =>
  (shipping && shipping.$type === 'checkoutPage') ?? false;

export const getEstimatedDeliveryProps = (
  checkoutState: CheckoutState,
  getSuggestedPostalCode: () => string | undefined,
): { estimation: EstimatedDeliveryProps['estimation'] } | undefined => {
  const formatDate = createDateFormatter(checkoutState);
  const { checkout, customer } = checkoutState;

  const postalCode = getSuggestedPostalCode();

  if (!isCheckoutPageShipping(checkout?.shipping) || !postalCode) {
    return undefined;
  }

  const estimatedDeliveryDates = formatEstimatedDeliveryDates(
    formatDate,
    checkout.shipping.selectedShippingMethod,
  );

  if (!estimatedDeliveryDates) {
    return undefined;
  }

  const formattedPostalCode = [postalCode, customer.address?.city].filter((x) => x).join(', ');

  return {
    estimation: {
      ...estimatedDeliveryDates,
      formattedPostalCode,
    },
  };
};

export const getOrderTotalProps = (
  checkoutState: CheckoutState,
  removeGiftCard: CheckoutActions['removeCode'],
): OrderTotalProps => {
  const formatCurrency = createCurrencyFormatter(checkoutState);
  const checkoutDetails = checkoutState.checkout;
  if (!checkoutDetails) {
    throw new Error('checkout details not loaded');
  }
  const savingsAmount = checkoutState.lines.reduce((acc, x) => {
    const originalTotal = x.price.originalTotal;

    if (!originalTotal) {
      return acc;
    }
    return originalTotal.amountInclTax - x.price.total.amountInclTax;
  }, 0);

  const checkoutShipping =
    checkoutDetails.shipping.$type === 'checkoutPage'
      ? checkoutDetails.shipping.selectedShippingMethod?.price.total.amountInclTax
      : undefined;

  const taxPercentage = checkoutState.checkout?.total.taxPercentage;

  return {
    total: formatCurrency(checkoutDetails.leftToPayInclTax),
    subTotal: formatCurrency(
      checkoutState.lines.reduce((acc, curr) => acc + curr.price.total.amountExclTax, 0),
    ),
    numOfItems: checkoutState.lines.reduce((acc, curr) => acc + curr.quantity, 0),
    shipping: checkoutShipping ? formatCurrency(checkoutShipping) : undefined,
    taxes: `${formatCurrency(
      checkoutDetails.total.amountInclTax - checkoutDetails.total.amountExclTax,
    )} (${taxPercentage}%)`,
    savings: savingsAmount !== 0 ? formatCurrency(savingsAmount) : undefined,
    discountCodes: checkoutState.discountCodes,
    giftCards: checkoutState.giftCards.map((x) => {
      const remaining = Math.abs(x.amount - (x.amountToUse ?? x.amount));
      return {
        amountFormatted: formatCurrency(-1 * Math.abs(x.amount)),
        remainingFormatted: remaining > 0 ? formatCurrency(x.amount - x.amountToUse) : undefined,
        code: x.code,
      };
    }),
    removeGiftCard,
    isExport: checkoutState.isExport,
  };
};

export const getPaymentMethodImages = (state: CheckoutState): string[] => {
  const { checkout } = state;

  if (!checkout?.payment || checkout.payment.$type !== 'checkoutPage') {
    return [];
  }

  const logos = checkout.payment.paymentMethods.flatMap(({ id, logoUrls }) =>
    id === 'klarna' || !id.includes('klarna') ? logoUrls : [],
  );

  return Array.from(new Set(logos));
};

// This type isn't exported?
export type CompletePurchaseClientResponse = Awaited<
  ReturnType<CheckoutActions['completePurchase']>
>;

export const parseCompletePurchaseResponse = (
  response: CompletePurchaseClientResponse,
  defaultMessage: string,
): { message: string } | undefined => {
  switch (response.$type) {
    case 'failed':
      return { message: response.errors.map((x) => x.message).join('\n') };
    case 'unexpectedError':
      return { message: defaultMessage };
    case 'validationError':
      return { message: defaultMessage };
    case 'noAction':
      return;
  }
};
